import { type FormEvent, type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { authzStore } from "../authz/authzStore";
import { detectBrowser } from "./detectBrowser";
import { InstructionsView } from "./instructions/InstructionsView";
import { jwtStore } from "./jwtStore";
import { useAuth } from "./AuthContext";

/**
 * The JWT-paste modal. PRD-002 DoD criteria implemented here:
 * - Surfaced when no JWT is held in app state and a protected action is attempted.
 * - Contains step-by-step instructions tailored to the user's browser.
 * - Accepts a pasted JWT string. Does NOT validate structure / signature / claims.
 *   The pasted value is stored as-is; the API rejects invalid tokens.
 *
 * Visual: matches the prototype's modal pattern (`requirements/ui-patterns.md`)
 * — dimmed navy overlay, white modal with header / body / footer rows,
 * Cancel + primary Save in the footer.
 */
export function JwtPasteModal() {
    const { jwt, isModalOpen, closeModal } = useAuth();
    const [value, setValue] = useState("");
    const overlayRef = useRef<HTMLDivElement>(null);
    const browser = useMemo(() => detectBrowser(), []);

    // PRD-002 §JWT-acquisition: while no JWT is cached, the modal is
    // non-dismissible — a paste is the only way out. Once a JWT IS cached
    // and the user reopens via the Authorize button (to swap tokens), the
    // usual dismiss paths reactivate so they can back out without losing
    // their current token.
    const dismissible = jwt !== null;

    useEffect(() => {
        if (!isModalOpen || !dismissible) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeModal();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isModalOpen, dismissible, closeModal]);

    if (!isModalOpen) return null;

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) return;
        // PRD-002 DoD: trust the paste. No parsing, no decoding, no claim check.
        // PRD-005: clearing the deny flag here lets a freshly-pasted token
        // recover the session — if the new token actually carries
        // Subject_Read the gate stack flips back to the happy path; if not,
        // the next 403 re-arms the flag.
        jwtStore.set(trimmed);
        authzStore.clear();
        setValue("");
        closeModal();
    };

    const handleCancel = () => {
        if (!dismissible) return;
        setValue("");
        closeModal();
    };

    const handleOverlayClick = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (!dismissible) return;
        if (e.target === overlayRef.current) handleCancel();
    };

    return (
        <div
            ref={overlayRef}
            className="modal-overlay"
            onClick={handleOverlayClick}
            data-testid="jwt-paste-modal"
        >
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="jwt-modal-title">
                <div className="modal-hdr">
                    <div>
                        <div className="modal-step">
                            <i className="fa-solid fa-lock-open" aria-hidden="true" /> Authorize
                        </div>
                        <div className="modal-title" id="jwt-modal-title">
                            Paste your patient-registry JWT
                        </div>
                    </div>
                    {dismissible && (
                        <button
                            type="button"
                            className="modal-x"
                            onClick={handleCancel}
                            aria-label="Close"
                            data-testid="jwt-modal-close"
                        >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                    )}
                </div>
                <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                    <div className="modal-body">
                        <InstructionsView browser={browser} />
                        <div className="field">
                            <label className="f-label" htmlFor="jwt-input-field">
                                JWT
                            </label>
                            <textarea
                                id="jwt-input-field"
                                className="f-textarea"
                                aria-label="JWT input"
                                data-testid="jwt-input"
                                placeholder="eyJhbGciOi…"
                                rows={4}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="modal-ftr">
                        {dismissible && (
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                data-testid="jwt-modal-cancel"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary"
                            data-testid="jwt-submit"
                            disabled={!value.trim()}
                        >
                            <i className="fa-solid fa-lock" aria-hidden="true" /> Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
